import { AxiosResponse } from 'axios';
import { config } from 'config';
import type { GGTheme } from 'themes/types';

import type { Membership } from 'types/membership';
import type { Package } from 'types/package';
import type { TPublicUser } from 'types/publicUser';
import type { TService } from 'types/service';

import { API_VERSIONS } from './constants';
import { getClient } from './utils/httpClient/httpClient.config';

type SalonResponse = {
  data: TPublicUser;
};

// price from the server is a string, but locally we expect a number
type APIService = {
  deposit_processing_fee: number;
  category_id: number;
  category_name: string;
  created_at: string;
  deposit_amount: number;
  deposit_for_new_clients_only?: boolean;
  description: string;
  end_duration: number;
  guid: string;
  image: string;
  name: string;
  ordering: number;
  price: string;
  price_hidden: boolean;
  price_varies: boolean;
  processing_duration: number;
  start_duration: number;
  total_duration: number;
};

type ServicesResponse = {
  data: APIService[];
};

type PackagesResponse = {
  packages: Package[];
  pagination: { nextCursor: string | null; size: number };
};

type MembershipsResponse = {
  memberships: Membership[];
  pagination: { nextCursor: string | null; size: number };
};

export const getSalonBySlug = async (slug: string) => {
  const url = `${config[API_VERSIONS.v3]}/web/salons/${slug}`;
  const { data: response }: AxiosResponse<SalonResponse> =
    await getClient().get(url, {
      params: { slug },
    });

  return response.data;
};

// TODO: guid is needed for BE to check feature flag for the salon.
// Remove guid when cleaning up OPTIMIZELY_FEATURES.SERVICE_OPTION
export const getServicesBySlug = async (
  slug: string,
  guid?: string,
): Promise<TService[]> => {
  const url = `${config[API_VERSIONS.v3]}/web/services`;
  const { data: response }: AxiosResponse<ServicesResponse> =
    await getClient().get(url, {
      params: { slug, guid },
    });

  return response.data.map((service: APIService) => {
    return {
      ...service,
      price: service?.price?.replace('$', '').replace(',', ''),
    };
  });
};

export const getThemeBySlug = async (slug: string) => {
  const url = `${config[API_VERSIONS.v3]}/web/salons/${slug}/theme`;
  const { data: response }: AxiosResponse<GGTheme> = await getClient().get(
    url,
    {
      params: { slug },
    },
  );

  return response;
};

export const getPackagesByBusinessToken = async (
  businessToken: string,
  isPackagesEnabled = false,
): Promise<Package[]> => {
  if (!isPackagesEnabled) {
    return [];
  }

  const url = `${config[API_VERSIONS.v4]}/web/packages`;
  const { data: response }: AxiosResponse<PackagesResponse> =
    await getClient().get(url, {
      params: { businessToken },
    });

  return response.packages;
};

export const getMembershipsByBusinessToken = async (
  businessToken: string,
  isMembershipsEnabled = false,
  orderToken?: string,
): Promise<Membership[]> => {
  if (!isMembershipsEnabled) {
    return [];
  }

  const url = `${config[API_VERSIONS.v4]}/web/memberships`;
  const { data: response }: AxiosResponse<MembershipsResponse> =
    await getClient().get(url, {
      params: { businessToken, ...(orderToken && { orderToken }) },
    });

  return response.memberships;
};
